export interface PaginatedResponse<T> extends Array<T> {
    total?: number;
    currentPage?: number;
    perPage?: number;
}

export interface FetchAllPagesOptions {
    pageSize?: number;
    startPage?: number;
}

export type PageFetcher<T> = (page: number, pageSize: number) => Promise<PaginatedResponse<T>>;

/**
 * Fetches all pages of paginated data by iterating through pages until complete.
 *
 * Termination conditions (whichever comes first):
 * 1. If `total` is provided: stops when accumulated items >= total
 * 2. If `total` is NOT provided: stops immediately after first page (backwards compatible)
 *
 * @param fetcher - Function that fetches a single page of data
 * @param options - Pagination configuration options
 * @returns Promise resolving to all accumulated items
 *
 * @example
 * ```typescript
 * const allItems = await fetchAllPages(
 *     (page, pageSize) => this.apiService.get(`/items`, { page, page_size: pageSize }),
 *     { pageSize: 50 }
 * );
 * ```
 */
export async function fetchAllPages<T> (
    fetcher: PageFetcher<T>,
    options: FetchAllPagesOptions = {},
): Promise<T[]> {
    const { pageSize = 100, startPage = 1 } = options;

    const allItems: T[] = [];
    let currentPage = startPage;
    let hasMorePages = true;

    while (hasMorePages) {
        const response = await fetcher(currentPage, pageSize);

        for (let i = 0; i < response.length; i++) {
            allItems.push(response[i]);
        }

        if (response.total === undefined || response.total === null) {
            break;
        }

        hasMorePages = allItems.length < response.total;
        currentPage++;
    }

    return allItems;
}
